import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import type { Immutable } from 'immer';
import type { CSSProperties, FC } from 'react';
import type { ManagedTrackerMessages } from '../../../store/trackers/trackers_reducers';
import { RelativeTime } from '../../BasicComponents/TimeDuration/RelativeTime';
import { TooltipWithUTCDatetime } from '../../BasicComponents/Tooltips/TooltipWithUTCDatetime';
import { IconCalendar, IconWaiting } from '../../Icon';
import {
  TRACKER_MESSAGE_HISTORY_RECORD_STATE_FRONTEND,
  getCurrentAttemptNumber,
  getCurrentMessageStatusCode,
  getMessageHistoryRecordInfo,
  getMostRecentMessageChangeDate,
  isMessageNotAcknowledged,
  isMessageNotSent,
} from './TrackerMessagesTableHelper';

interface Props {
  message: Immutable<ManagedTrackerMessages>;
}

export const StatusText: FC<Props> = ({ message }) => {
  const isNotSent = isMessageNotSent(message);
  const isNotAck = isMessageNotAcknowledged(message);

  let messageStatusCode;
  if (isNotSent) {
    messageStatusCode = TRACKER_MESSAGE_HISTORY_RECORD_STATE_FRONTEND.NOT_SENT;
  } else if (isNotAck) {
    messageStatusCode = TRACKER_MESSAGE_HISTORY_RECORD_STATE_FRONTEND.NOT_ACKNOWLEDGED;
  } else {
    messageStatusCode = getCurrentMessageStatusCode(message);
  }

  const messageHistoryRecordTableInfo = getMessageHistoryRecordInfo(
    messageStatusCode,
    getCurrentAttemptNumber(message),
  );
  if (messageHistoryRecordTableInfo) {
    return (
      <Container color={messageHistoryRecordTableInfo.color}>
        {messageHistoryRecordTableInfo.title !== 'Pending' ? (
          <>
            {messageHistoryRecordTableInfo.icon}
            <Text
              time={
                isNotSent || isNotAck
                  ? message.closeAfter.toISOString()
                  : getMostRecentMessageChangeDate(message).toISOString()
              }
            >
              {messageHistoryRecordTableInfo.title}
            </Text>
          </>
        ) : message.plannedAt.valueOf() > Date.now() ? (
          <>
            <IconCalendar fixedWidth />
            <Text time={message.plannedAt.toISOString()}>Planned For</Text>
          </>
        ) : (
          <>
            <IconWaiting style={{ color: RacemapColors.DarkGray }} fixedWidth />
            <div>Pending</div>
          </>
        )}
      </Container>
    );
  }
  return <Container />;
};

const Text: FC<{ children: string; time: string }> = ({ children, time }) => {
  return (
    <TextContainer>
      <TextField>{children}</TextField>
      <TooltipWithUTCDatetime datetime={time}>
        <TimeField>
          <RelativeTime time={time} />
        </TimeField>
      </TooltipWithUTCDatetime>
    </TextContainer>
  );
};

const TextContainer = styled.div`
  margin-left: 7px;
`;
const TimeField = styled.div`
  font-weight: 300;
  opacity: 0.7;
  font-size: 0.9rem;
`;
const TextField = styled.div`
  height: 1.2rem;
`;

const Container = styled.div<{ color?: CSSProperties['color'] }>`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  font-weight: 600;
  color: ${({ color }) => (color == null ? RacemapColors.DarkGray : color)};
`;
